import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  ArrowsOutSimple,
  ArrowsInSimple,
  ArrowsClockwise,
  FilePdf,
  ChartBar,
  ShoppingCart,
  Megaphone,
  MagnifyingGlass,
  Sparkle,
  Wrench,
} from "@phosphor-icons/react";
import PerformanceHero from "./PerformanceHero";
import FreshnessBar from "./FreshnessBar";
import BehaviourIntelligence from "./BehaviourIntelligence";
import CommerceInsights from "../CommerceInsights";
import ClientAdCampaigns from "../ClientAdCampaigns";
import GoogleAdsWidget from "./GoogleAdsWidget";
import SearchConsoleWidget from "./SearchConsoleWidget";
import ExecutiveOverview from "./ExecutiveOverview";
import AiAdvisorFlagship from "./AiAdvisorFlagship";
import FunnelDropOff from "./FunnelDropOff";
import { SkeletonCard } from "./Skeleton";
import ErrorBoundary from "./ErrorBoundary";
import { useAuth } from "../../AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── IST helpers for the date-range presets ──
const IST_OFFSET_MIN = 330;
const istToday = () => {
  const now = new Date();
  return new Date(now.getTime() + (now.getTimezoneOffset() + IST_OFFSET_MIN) * 60000);
};
const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

const DownloadPdfButton = ({ clientId, month, startDate, endDate, clientName }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const params = {};
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.month = month;
      }
      const res = await axios.get(
        `${API}/intelligence/${clientId}/executive-pdf`,
        { params, responseType: "blob", timeout: 90000 }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (clientName || "client").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40);
      const stamp = startDate && endDate ? `${startDate}_to_${endDate}` : (month || "");
      a.download = `${safe}_executive_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || e.message || "PDF generation failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] disabled:opacity-50 transition-colors"
      data-testid="pi-download-pdf"
      title="Download McKinsey-style executive PDF report"
    >
      <FilePdf size={13} /> {busy ? "Building PDF…" : "Download PDF"}
    </button>
  );
};

const MAX_LOOKBACK_DAYS = 180;
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

const computeRange = (preset, month, customStart, customEnd) => {
  const today = istToday();
  const todayStr = fmtISO(today);
  if (preset === "today") {
    return { start_date: todayStr, end_date: todayStr, label: "Today", preset };
  }
  if (preset === "7d") {
    const start = new Date(today.getTime() - 6 * 86400000);
    return { start_date: fmtISO(start), end_date: todayStr, label: "Last 7 days", preset };
  }
  if (preset === "30d") {
    const start = new Date(today.getTime() - 29 * 86400000);
    return { start_date: fmtISO(start), end_date: todayStr, label: "Last 30 days", preset };
  }
  if (preset === "custom") {
    if (!customStart || !customEnd) return null;
    return { start_date: customStart, end_date: customEnd, label: "Custom range", preset };
  }
  if (month) {
    const [y, m] = month.split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const lastDayOfMonth = new Date(y, m, 0);
    const isCurrentMonth = today.getFullYear() === y && today.getMonth() === m - 1;
    const end = isCurrentMonth ? today : lastDayOfMonth;
    return { start_date: fmtISO(start), end_date: fmtISO(end), label: "This Month", preset };
  }
  return null;
};

// ── Tab configuration ──
const TABS = [
  { key: "overview", label: "Overview", Icon: ChartBar, hint: "Executive dashboard" },
  { key: "commerce", label: "Commerce", Icon: ShoppingCart, hint: "What customers buy" },
  { key: "marketing", label: "Marketing", Icon: Megaphone, hint: "Where revenue comes from" },
  { key: "seo", label: "SEO", Icon: MagnifyingGlass, hint: "Organic search performance" },
  { key: "ai-advisor", label: "AI Advisor", Icon: Sparkle, hint: "Recommendations & priorities" },
  { key: "advanced", label: "Advanced", Icon: Wrench, hint: "Raw tables & drill-downs" },
];

const TabPill = ({ tab, active, onClick }) => {
  const { Icon, label, hint, key } = tab;
  return (
    <button
      onClick={onClick}
      className={`group flex items-center gap-2 px-3.5 py-2 rounded-lg border transition-all whitespace-nowrap ${
        active
          ? "bg-[#FFEB00] text-black border-[#FFEB00] font-semibold shadow-[0_0_0_1px_rgba(255,235,0,0.35)]"
          : "bg-white/[0.02] text-white/70 border-white/10 hover:bg-white/[0.05] hover:text-white"
      }`}
      title={hint}
      data-testid={`pi-tab-${key}`}
    >
      <Icon size={15} weight={active ? "fill" : "regular"} />
      <span className="text-[12px]">{label}</span>
    </button>
  );
};

// ── Marketing tab: merges Traffic + Meta Ads + Google Ads ──
const MarketingTab = ({ clientId, currency, month, dateRange, isClientUser, isGAConnected, intel, loadingIntel, loadingKpi, kpi }) => {
  const aov = kpi?.shopify?.aov || 0;
  return (
    <div className="space-y-6" data-testid="marketing-tab">
      {/* Phase 4 · biggest funnel drop-off callout at the top */}
      <ErrorBoundary label="Funnel Intelligence">
        <FunnelDropOff clientId={clientId} dateRange={dateRange} currency={currency} avgOrderValue={aov} />
      </ErrorBoundary>

      <div className="rounded-xl border border-white/10 bg-[#0B0B0F] p-5" data-testid="marketing-traffic">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>Traffic & Acquisition</h3>
            <p className="text-[11px] text-white/50 mt-0.5">GA4 — sessions · channels · landing pages · devices · funnel · source quality</p>
          </div>
        </div>
        <ErrorBoundary label="Traffic & Acquisition">
          {loadingKpi ? <SkeletonCard rows={6} /> : (
            <BehaviourIntelligence
              clientId={clientId}
              currency={currency}
              gaConnected={isGAConnected}
              dateRange={dateRange}
              trafficIntel={intel?.widgets?.traffic}
              loadingIntel={loadingIntel}
            />
          )}
        </ErrorBoundary>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0B0B0F] p-5" data-testid="marketing-meta">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>Meta Ads</h3>
            <p className="text-[11px] text-white/50 mt-0.5">Facebook + Instagram — spend · CTR · CPC · CPM · creatives · campaign performance</p>
          </div>
        </div>
        <ErrorBoundary label="Meta Ads">
          <ClientAdCampaigns
            clientId={clientId}
            currency={currency}
            month={month}
            dateRange={dateRange}
            embedded
            readOnly={isClientUser}
            paidMediaIntel={intel?.widgets?.paid_media}
            loadingIntel={loadingIntel}
          />
        </ErrorBoundary>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0B0B0F] p-5" data-testid="marketing-google-ads">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>Google Ads</h3>
            <p className="text-[11px] text-white/50 mt-0.5">Search + Display — spend · clicks · conversions · campaign ROAS · AI brief</p>
          </div>
        </div>
        <ErrorBoundary label="Google Ads">
          <GoogleAdsWidget clientId={clientId} currency={currency} dateRange={dateRange} />
        </ErrorBoundary>
      </div>
    </div>
  );
};

// ── Advanced tab: placeholder for raw exports and technical drill-downs ──
const AdvancedTab = ({ clientId, currency, dateRange }) => {
  return (
    <div className="space-y-5" data-testid="advanced-tab">
      <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10">
        <h3 className="text-base font-semibold text-white mb-1.5" style={{ fontFamily: "Varela Round, sans-serif" }}>Advanced Analytics</h3>
        <p className="text-[12px] text-white/60 leading-relaxed">
          Raw tables, per-source exports and technical drill-downs. Everything on this tab is designed for power users
          and internal team members who need to inspect the source data behind the executive dashboards.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AdvancedTile
          title="Landing Page Reports"
          hint="Top pages by sessions, engagement rate, conversions and revenue attribution."
          badge="GA4"
        />
        <AdvancedTile
          title="Browser & Device Reports"
          hint="Per-browser CVR anomalies, mobile-vs-desktop deltas, in-app browser drop-offs."
          badge="GA4"
        />
        <AdvancedTile
          title="Keyword & Search Appearance"
          hint="Query-level winners/losers, cannibalization, sitelinks/videos/FAQs impact."
          badge="GSC"
        />
        <AdvancedTile
          title="Source Quality Deep Dive"
          hint="Bot filtering, spam referrals, session-quality distribution."
          badge="GA4"
        />
        <AdvancedTile
          title="Campaign Breakdowns"
          hint="Per-campaign, per-adset, per-creative table with ROAS, CPA and frequency."
          badge="Meta · GAds"
        />
        <AdvancedTile
          title="Debug & Raw Exports"
          hint="CSV / JSON exports for every widget on the platform — for BI-team ingest."
          badge="All sources"
        />
      </div>

      <div className="p-4 rounded-xl bg-gradient-to-r from-[#FFEB0011] to-transparent border border-[#FFEB0022]">
        <p className="text-xs text-white/80">
          <strong>Coming next (Phase 4):</strong> the tiles above become fully interactive tables with filters,
          column sorting, per-row AI diagnosis and one-click CSV/PDF export.
        </p>
      </div>
    </div>
  );
};

const AdvancedTile = ({ title, hint, badge }) => (
  <div className="p-4 rounded-xl bg-[#0B0B0F] border border-white/10 hover:border-white/20 transition-colors">
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-semibold text-white">{title}</h4>
      <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] border border-white/10 text-white/60 uppercase tracking-wider">{badge}</span>
    </div>
    <p className="text-[11px] text-white/55 leading-relaxed">{hint}</p>
    <p className="text-[10px] text-white/35 mt-3 italic">Available in Phase 4</p>
  </div>
);

const PerformanceIntelligence = ({ clientId, clientName, currency, month, monthLabel, gaConnected }) => {
  const { user } = useAuth();
  const isClientUser = user?.role === "client_user";

  const [kpi, setKpi] = useState(null);
  const [loadingKpi, setLoadingKpi] = useState(true);
  const [presentation, setPresentation] = useState(false);
  const [rangePreset, setRangePreset] = useState("this_month");
  const [intel, setIntel] = useState(null);
  const [loadingIntel, setLoadingIntel] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Custom date range state (only used when rangePreset === "custom")
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [spendSummary, setSpendSummary] = useState(null);
  const todayStr = fmtISO(istToday());
  const minStartStr = fmtISO(new Date(istToday().getTime() - MAX_LOOKBACK_DAYS * 86400000));
  const customError = useMemo(() => {
    if (rangePreset !== "custom") return null;
    if (!customStart || !customEnd) return null;
    if (customStart > customEnd) return "Start date must be before end date.";
    if (customEnd > todayStr) return "End date cannot be in the future.";
    if (daysBetween(customStart, todayStr) > MAX_LOOKBACK_DAYS) {
      return `Start date can go back a maximum of ${MAX_LOOKBACK_DAYS} days.`;
    }
    return null;
  }, [rangePreset, customStart, customEnd, todayStr]);

  const dateRange = useMemo(
    () => computeRange(rangePreset, month, customStart, customEnd),
    [rangePreset, month, customStart, customEnd]
  );

  const loadIntel = useCallback(async () => {
    if (!dateRange || customError) { setIntel(null); return; }
    setLoadingIntel(true);
    try {
      const res = await axios.get(`${API}/intelligence/${clientId}/insights`, {
        params: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          label: dateRange.label,
          preset: dateRange.preset,
        },
      });
      setIntel(res.data || null);
    } catch {
      setIntel(null);
    } finally {
      setLoadingIntel(false);
    }
  }, [clientId, dateRange?.start_date, dateRange?.end_date, dateRange?.label, dateRange?.preset, customError]);

  useEffect(() => { loadIntel(); }, [loadIntel]);

  const loadKpi = useCallback(async (force = false) => {
    if (customError) { setKpi(null); setLoadingKpi(false); return; }
    setLoadingKpi(true);
    try {
      const params = { month, force_refresh: force };
      if (dateRange && rangePreset !== "this_month") {
        params.start_date = dateRange.start_date;
        params.end_date = dateRange.end_date;
      }
      const res = await axios.get(`${API}/intelligence/${clientId}/executive-kpi`, { params });
      setKpi(res.data);
    } catch {
      setKpi(null);
    } finally {
      setLoadingKpi(false);
    }
  }, [clientId, month, dateRange, rangePreset, customError]);

  useEffect(() => { loadKpi(false); }, [loadKpi]);

  // Load spend summary (Phase 2 aggregator) — used by Overview and the strip
  useEffect(() => {
    if (!dateRange || customError) { setSpendSummary(null); return; }
    let cancelled = false;
    axios.get(`${API}/clients/${clientId}/spend-summary`, {
      params: { start: dateRange.start_date, end: dateRange.end_date },
    }).then(r => { if (!cancelled) setSpendSummary(r.data); })
      .catch(() => { if (!cancelled) setSpendSummary(null); });
    return () => { cancelled = true; };
  }, [clientId, dateRange?.start_date, dateRange?.end_date, customError]);

  useEffect(() => {
    if (presentation) document.body.classList.add("presentation-mode");
    else document.body.classList.remove("presentation-mode");
    return () => document.body.classList.remove("presentation-mode");
  }, [presentation]);

  const resyncAll = async () => {
    try {
      await axios.post(`${API}/intelligence/${clientId}/resync`);
      await loadKpi(true);
      window.location.reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Resync failed");
    }
  };

  const isGAConnected = gaConnected !== undefined ? gaConnected : !(kpi?.ga4?.error);

  const heroBlock = (
    <ErrorBoundary label="the hero section">
      <PerformanceHero
        clientId={clientId}
        clientName={clientName}
        month={month}
        monthLabel={dateRange ? dateRange.label : monthLabel}
        currency={currency}
        kpi={kpi}
        loadingKpi={loadingKpi}
        dateRange={dateRange}
        intel={intel}
        loadingIntel={loadingIntel}
      />
    </ErrorBoundary>
  );

  return (
    <div className="space-y-5 pb-12" data-testid="performance-intelligence">
      {/* Date range filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 pi-range-filter" data-testid="pi-range-filter">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/10 rounded p-1">
            {[
              ["today", "Today"],
              ["7d", "Last 7d"],
              ["30d", "Last 30d"],
              ["this_month", "This Month"],
              ["custom", "Custom"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setRangePreset(k)}
                className={`px-3 py-1 text-[11px] rounded transition-colors ${
                  rangePreset === k ? "bg-[#FFEB00] text-black font-semibold" : "text-white/70 hover:text-white"
                }`}
                data-testid={`pi-range-${k}`}
              >
                {label}
              </button>
            ))}
          </div>

          {rangePreset === "custom" && (
            <div className="flex items-center gap-2" data-testid="pi-range-custom-inputs">
              <input
                type="date"
                value={customStart}
                min={minStartStr}
                max={todayStr}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-[11px] text-white/90 [color-scheme:dark] focus:outline-none focus:border-[#FFEB00]/60"
                data-testid="pi-range-custom-start"
              />
              <span className="text-white/40 text-[11px]">→</span>
              <input
                type="date"
                value={customEnd}
                min={customStart || minStartStr}
                max={todayStr}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-[11px] text-white/90 [color-scheme:dark] focus:outline-none focus:border-[#FFEB00]/60"
                data-testid="pi-range-custom-end"
              />
              <span className="text-[10px] text-white/40 italic hidden sm:inline">Max 180 days back</span>
            </div>
          )}
        </div>
        {dateRange && !customError && (
          <span className="text-[10px] text-white/50 uppercase tracking-wider" data-testid="pi-range-label">
            {dateRange.label} · {dateRange.start_date} → {dateRange.end_date}
          </span>
        )}
        {customError && (
          <span className="text-[10px] text-red-400 uppercase tracking-wider" data-testid="pi-range-error">
            {customError}
          </span>
        )}
      </div>

      {/* Utility bar */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-internal-control="true">
        {!isClientUser ? <FreshnessBar clientId={clientId} month={month} /> : <div />}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPresentation(p => !p)}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] transition-colors"
            data-testid="pi-presentation-toggle"
            title="Toggle presentation mode (hides sidebar, larger type)"
          >
            {presentation ? <ArrowsInSimple size={13} /> : <ArrowsOutSimple size={13} />}
            {presentation ? "Exit Presentation" : "Presentation Mode"}
          </button>
          {!isClientUser && (
            <button
              onClick={resyncAll}
              className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:bg-white/[0.04] transition-colors"
              data-testid="pi-resync-all"
            >
              <ArrowsClockwise size={13} /> Resync all
            </button>
          )}
          <DownloadPdfButton
            clientId={clientId}
            month={month}
            startDate={dateRange?.start_date}
            endDate={dateRange?.end_date}
            clientName={clientName}
          />
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-white/10 pb-3 -mx-1 px-1 overflow-x-auto" data-testid="pi-tab-nav">
        <div className="flex items-center gap-2 min-w-max">
          {TABS.filter(t => !(t.key === "advanced" && isClientUser)).map(tab => (
            <TabPill
              key={tab.key}
              tab={tab}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            />
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="fade-up">
        {activeTab === "overview" && (
          <ErrorBoundary label="Executive Overview">
            <ExecutiveOverview
              clientId={clientId}
              clientName={clientName}
              currency={currency}
              dateRange={dateRange}
              kpi={kpi}
              intel={intel}
              spendSummary={spendSummary}
              loadingIntel={loadingIntel}
              onNavigateToTab={(t) => setActiveTab(t)}
              detailedHero={heroBlock}
            />
          </ErrorBoundary>
        )}

        {activeTab === "commerce" && (
          <div className="rounded-xl border border-white/10 bg-[#0B0B0F] p-5" data-testid="commerce-tab">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>Commerce Intelligence</h3>
              <p className="text-[11px] text-white/50 mt-0.5">Shopify — daily revenue · best sellers · collections · geography · buying time · customers</p>
            </div>
            <ErrorBoundary label="Commerce Intelligence">
              {loadingKpi ? <SkeletonCard rows={8} /> : (
                <CommerceInsights
                  clientId={clientId}
                  currency={currency}
                  month={month}
                  dateRange={dateRange}
                  readOnly={isClientUser}
                  commerceIntel={intel?.widgets?.commerce}
                  customersIntel={intel?.widgets?.customers}
                  loadingIntel={loadingIntel}
                />
              )}
            </ErrorBoundary>
          </div>
        )}

        {activeTab === "marketing" && (
          <MarketingTab
            clientId={clientId}
            currency={currency}
            month={month}
            dateRange={dateRange}
            isClientUser={isClientUser}
            isGAConnected={isGAConnected}
            intel={intel}
            loadingIntel={loadingIntel}
            loadingKpi={loadingKpi}
            kpi={kpi}
          />
        )}

        {activeTab === "seo" && (
          <div className="rounded-xl border border-white/10 bg-[#0B0B0F] p-5" data-testid="seo-tab">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>SEO Intelligence</h3>
              <p className="text-[11px] text-white/50 mt-0.5">Google Search Console + Technical SEO — clicks · impressions · CTR · position · Core Web Vitals · AI brief</p>
            </div>
            <ErrorBoundary label="SEO Intelligence">
              <SearchConsoleWidget clientId={clientId} dateRange={dateRange} />
            </ErrorBoundary>
          </div>
        )}

        {activeTab === "ai-advisor" && (
          <div className="rounded-xl border border-white/10 bg-[#0B0B0F] p-5" data-testid="ai-advisor-tab">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Varela Round, sans-serif" }}>AI Advisor</h3>
              <p className="text-[11px] text-white/50 mt-0.5">Ranked wins · risks · opportunities · recommendations — click any card to deep-link into its source report</p>
            </div>
            <ErrorBoundary label="AI Advisor">
              <AiAdvisorFlagship
                intel={intel}
                loading={loadingIntel}
                onNavigateToTab={(t) => setActiveTab(t)}
              />
            </ErrorBoundary>
          </div>
        )}

        {activeTab === "advanced" && !isClientUser && (
          <AdvancedTab clientId={clientId} currency={currency} dateRange={dateRange} />
        )}
      </div>
    </div>
  );
};

export default PerformanceIntelligence;
